import React, { forwardRef, MutableRefObject, useEffect, useRef } from "react";
import { css } from "@emotion/react";
import { styles } from "../styles";
import { Display } from "../../Icons";
import Icon from "../../Icon";
import { DoNotCare } from "../../types";
import { ListItemProps } from "../types";

const DEPTH_COUNT = 20;

/**
 * Utility function to focus the next item in a list.
 * @param event - The event that triggered the focus change.
 */
const focusNextItem = (
  event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>
) => {
  let next: DoNotCare = event.currentTarget?.nextElementSibling;
  for (let i = 0; i < DEPTH_COUNT; i++) {
    if (
      !next?.getAttribute("aria-selected") ||
      next?.getAttribute("aria-disabled") === "true"
    ) {
      next = next?.nextElementSibling;
    }
  }
  next?.focus();
};

/**
 * Utility function to focus the search input in a list.
 * @param event - The event that triggered the focus change.
 */
const focusSearchInput = (
  event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>
) => {
  let parentElem = event.currentTarget?.parentElement;
  let searchInput: HTMLInputElement | null = null;
  for (let i = 0; i < DEPTH_COUNT && !searchInput; i++) {
    if (!parentElem) {
      break;
    }
    searchInput = parentElem.querySelector<HTMLInputElement>(
      ".list-search-input input"
    );
    if (!searchInput) {
      parentElem = parentElem.parentElement;
    }
  }
  searchInput?.focus();
};

/**
 * Utility function to focus the previous item in a list.
 * @param event - The event that triggered the focus change.
 */
const focusPreviousItem = (
  event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>
) => {
  let prevSibling: DoNotCare = event.currentTarget?.previousElementSibling;
  for (let i = 0; i < DEPTH_COUNT; i++) {
    if (
      prevSibling?.getAttribute("aria-selected") &&
      prevSibling?.getAttribute("aria-disabled") !== "true"
    ) {
      break;
    }
    prevSibling = prevSibling?.previousElementSibling;
  }
  if (prevSibling) {
    prevSibling.focus();
    return;
  }
  focusSearchInput(event);
};

/**
 * `List.Item` is used to render a single item in a list.
 * @example
 * ```tsx
 * <List>
 *   <List.Item>Thing #1</List.Item>
 *   <List.Item onClick={doSomething}>Thing #2</List.Item>
 * </List>
 * ```
 */
const ListItem = forwardRef(function ListItem(
  {
    id,
    label,
    children = label,
    prefix,
    cannotClear = false,
    isSelected = false,
    disabled = false,
    selectable = false,
    section,
    focusOnMount = false,
    onClick,
    onKeyDown,
    onKeyUp,
    onFocus,
    onBlur
  }: ListItemProps,
  ref: MutableRefObject<HTMLLIElement | null>
) {
  let role: "listitem" | "option" | "group" = "listitem";
  if (section) {
    role = "group";
  } else if (selectable) {
    role = "option";
  }

  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled || !id || (cannotClear && isSelected)) {
      return;
    }
    event.currentTarget.blur();
    onClick?.(event);
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (id && ["Enter", " "].includes(event.key)) {
      if (disabled || (cannotClear && isSelected)) {
        return;
      }
      focusNextItem(event);
      onClick?.(event as DoNotCare);
    }
    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === "ArrowDown") {
        focusNextItem(event);
      }
      if (event.key === "ArrowUp") {
        focusPreviousItem(event);
      }
    }
    onKeyDown?.(event);
  };

  const itemRef = useRef<HTMLLIElement | null>(null);

  const interactiveProps =
    role === "listitem" || disabled
      ? {}
      : {
          onClick: handleOnClick,
          onKeyDown: handleOnKeyDown,
          onKeyUp,
          onFocus,
          onBlur,

          tabIndex: 0
        };

  useEffect(() => {
    if (focusOnMount) {
      itemRef.current?.focus();
    }
  }, []);

  return (
    <li
      className="kit-ListItem"
      ref={current => {
        const propsRef = ref;
        if (propsRef) {
          propsRef.current = current;
        }
        itemRef.current = current;
      }}
      role={role}
      id={typeof id === "string" ? id : undefined}
      {...(!!section && { ["aria-labelledby"]: section })}
      {...interactiveProps}
      css={[
        styles.itemWrapper,
        !disabled && (onClick || selectable) && styles.isClickable,
        isSelected && styles.selected,
        !cannotClear && isSelected && !disabled && styles.clearableSelected,
        disabled && styles.isDisabled
      ]}
      aria-label={label}
      aria-disabled={disabled}
      aria-selected={isSelected}
    >
      <div className="item-inner" css={[styles.item]}>
        <div css={[styles.iconAndLabel]}>
          {!!prefix && (
            <Display size="small">
              {typeof prefix === "string" ? (
                <Icon name={prefix} size="fill" />
              ) : (
                prefix
              )}
            </Display>
          )}

          <div css={[styles.itemContent]}>{children}</div>
        </div>

        {!cannotClear && isSelected && (
          <div
            className="clear-item-icon"
            tabIndex={-1}
            css={css`
              display: contents;
            `}
          >
            <Icon name="Circled.X" fill="var(--color-secondary)" />
          </div>
        )}
      </div>
    </li>
  );
});

ListItem.displayName = "List.Item";
export default ListItem;
